import { useQuery } from '@tanstack/react-query';
import { Button, Descriptions, Divider, Flex, Grid, Spin, Tabs, Typography } from 'antd';
import type { CollapseProps } from 'antd';
import Parser from 'html-react-parser';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getTicket } from 'src/apis/helpdesk/ticket.api';
import { getBusinessTripTicket } from 'src/apis/hr_ticket/businessTripTicket.api';
import { getTransportationTicket } from 'src/apis/hr_ticket/transportationTicket.api';
import { BtnHrHandle } from 'src/components/v2/Button';
import CollapseCustom from 'src/components/v2/Collapse';
import { HRTicketStatusTag, TicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import { TransportationTypeEn } from 'src/constants/transportationType.enum';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { TicketStatus } from 'src/types/helpdesk/ticket.type';
import type { ITransportationTicketDetail } from 'src/types/hr_ticket/transportationTicket.type';
import { formatDate, formatToVND } from 'src/utils/utils';
import './transportationTicketDetail.scss';

const { Title, Text } = Typography;

type ActiveType = '1' | '2';

export default function TransportationTicketDetail() {
  const { setInvalidKey } = useContext(TicketContext);
  const [activeKey, setActiveKey] = useState<ActiveType>('1');
  const { setTransportationTicket, setLoading } = useContext(HrTicketDetailContext);
  const { ticketSelected, setIsTicketDetailCollapsed } = useContext(AppContext);
  const [refTicketId, setRefTicketId] = useState<string | undefined>(undefined);
  const [businessTripTicketId, setBusinessTripTicketId] = useState<number | undefined>(undefined);
  const { id } = useParams();

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ['transportationTicket', id],
    queryFn: () => getTransportationTicket(Number(id)),
    onSuccess: (data) => {
      setRefTicketId(data.data.refTicketId ?? undefined);
      setBusinessTripTicketId(data.data.businessTripTicketId ? Number(data.data.businessTripTicketId) : undefined);
    },
    enabled: !!id && !!ticketSelected
  });

  const businessTripTicketQuery = useQuery({
    queryKey: ['businessTripTicket', businessTripTicketId],
    queryFn: () => getBusinessTripTicket(businessTripTicketId as number),
    enabled: !!businessTripTicketId && !!id,
    staleTime: 60 * 1000
  });

  const refTicketQuery = useQuery({
    queryKey: ['ticket', refTicketId],
    queryFn: () => getTicket(refTicketId ?? ''),
    enabled: !!refTicketId && !!id,
    staleTime: 60 * 1000
  });

  useEffect(() => {
    if (data) {
      setTransportationTicket(data);
      setLoading(isFetching);
      setInvalidKey(JSON.stringify(data?.data));
    }
  }, [data, isFetching, setTransportationTicket, setLoading, setInvalidKey]);

  useEffect(() => {
    if (!id) {
      setIsTicketDetailCollapsed(true);
    }
  }, [id, setIsTicketDetailCollapsed]);

  const transportationTicketDetailItems = (
    transportationTicketDetails: ITransportationTicketDetail[]
  ): CollapseProps['items'] => {
    return transportationTicketDetails.map((transportationTicketDetail) => ({
      key: transportationTicketDetail.id,
      label: (
        <Descriptions
          layout='horizontal'
          labelStyle={{ width: '135px' }}
          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
        >
          <Descriptions.Item label='Loại đối tượng'>
            {transportationTicketDetail.transportedObjectType.name ?? ''}
          </Descriptions.Item>
          <Descriptions.Item label='Đối tượng'>
            {transportationTicketDetail.transportedObjectTypeId === 3
              ? transportationTicketDetail?.employee?.fullName
              : transportationTicketDetail.transportedObject}
          </Descriptions.Item>
          <Descriptions.Item label='Đơn vị tính'>{transportationTicketDetail.uoM ?? ''}</Descriptions.Item>
          <Descriptions.Item label='Số lượng'>{transportationTicketDetail.quantity ?? ''}</Descriptions.Item>
          <Descriptions.Item label='Khối lượng (KG)'>{transportationTicketDetail.weight ?? ''}</Descriptions.Item>
        </Descriptions>
      ),
      showArrow: false,
      className: 'transportation-ticket-item'
    }));
  };

  // const renderStatus = (item: ITransportationTicket | undefined) => {
  //   if (item?.ticketStatusId === TicketStatus.ApprovedRequest) {
  //     if (basePath?.includes(urlPath.hrConfirmTransportationTicket.location)) {
  //       switch (item?.confirmedType) {
  //         case ConfirmTypeEn.Confirmed:
  //           return <Tag color='var(--color-approved-request)'>Đã xác nhận</Tag>;
  //         case ConfirmTypeEn.Reject:
  //           return <Tag color='var(--color-rejected)'>Đã từ chối</Tag>;
  //         case ConfirmTypeEn.WaitingConfirm:
  //           return <Tag color='var(--color-new)'>Chờ xác nhận</Tag>;
  //         default:
  //           break;
  //       }
  //     } else if (basePath?.includes(urlPath.transportationTicket.location)) {
  //       switch (item?.confirmedType) {
  //         case ConfirmTypeEn.Confirmed:
  //           return <Tag color='var(--color-approved-request)'>Hoàn thành</Tag>;
  //         case ConfirmTypeEn.Reject:
  //           return <Tag color='var(--color-rejected)'>Đã từ chối</Tag>;
  //         case ConfirmTypeEn.WaitingConfirm:
  //           return <Tag color='var(--color-new)'>Chờ xác nhận</Tag>;

  //         default:
  //           break;
  //       }
  //     }
  //   } else if (item?.ticketStatusId === TicketStatus.Reject) {
  //     return <Tag color='var(--color-rejected)'>{item?.ticketStatus.name}</Tag>;
  //   } else if (item?.ticketStatusId === TicketStatus.Canceled) {
  //     return <Tag color='var(--color-canceled)'>{item?.ticketStatus.name}</Tag>;
  //   } else return <Tag color='var(--color-new)'>{item?.ticketStatus.name}</Tag>;
  // };

  const tabsItems = () => {
    const items = [
      {
        label: 'Phiếu vận chuyển',
        key: '1' as ActiveType,
        children: (
          <Fragment>
            <Flex justify='start'>
              <Title level={5}>Thông tin phiếu</Title>
            </Flex>
            <Spin size='small' spinning={isLoading}>
              <Descriptions
                layout='horizontal'
                labelStyle={{ width: '135px' }}
                column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
              >
                <Descriptions.Item label='Số phiếu'>
                  <Text strong>{data?.data?.code}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={businessTripTicketId ? 'Số phiếu công tác' : 'Số ticket'}>
                  {(data?.data?.refTicketCode || data?.data?.businessTripTicket?.code) && (
                    <Button type='link' onClick={() => setActiveKey('2')}>
                      {data?.data?.refTicketCode ? data?.data?.refTicketCode : data?.data?.businessTripTicket?.code}
                    </Button>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label='Ngày tạo'>
                  <Text>{formatDate(data?.data?.createdDate)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Trạng thái'>
                  <HRTicketStatusTag
                    ticketStatus={data?.data?.ticketStatus}
                    confirmedType={data?.data?.confirmedType}
                  />
                </Descriptions.Item>
                <Descriptions.Item label='Người tạo'>
                  <Text>{data?.data?.createdBy?.fullName}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Loại đề nghị'>
                  <Text>{data?.data?.requestType?.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Bộ phận'>
                  <Text>{data?.data?.createdBy?.department?.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Mã dự án'>
                  <Text>{data?.data?.projectCode}</Text>
                </Descriptions.Item>
              </Descriptions>
              <Flex vertical gap={8}>
                <Descriptions
                  layout='horizontal'
                  labelStyle={{ width: '135px' }}
                  column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
                  className='transportation-info-before'
                  style={{
                    backgroundColor: !!data?.data.transportationTicketVerify ? '#fbe9eb' : '',
                    border: !!data?.data.transportationTicketVerify ? 'thin solid rgba(255,77,77,.1)' : ''
                  }}
                  extra={
                    !!data?.data.transportationTicketVerify && (
                      <Text style={{ position: 'absolute', right: 10, top: 10 }} type='danger'>
                        Thông tin đăng ký
                      </Text>
                    )
                  }
                >
                  <Descriptions.Item label='Lý do đề nghị' span={colspan}>
                    <Text strong>{data?.data?.note}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Nơi đi' span={colspan}>
                    <Text>
                      {renderLocationText(
                        data?.data?.departureAddress,
                        data?.data?.distance?.startingLocation,
                        data?.data?.departureIdInString,
                        'Chưa có thông tin điểm đi'
                      )}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Nơi đến' span={colspan}>
                    <Text>
                      {renderLocationText(
                        data?.data?.destinationAddress,
                        data?.data?.distance?.destinationLocation,
                        data?.data?.destinationIdInString,
                        'Chưa có thông tin điểm đến'
                      )}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Khoảng cách' span={colspan}>
                    <Text strong>{data?.data?.totalKm ? data?.data?.totalKm + ' KM' : ''}</Text>
                  </Descriptions.Item>
                  {data?.data?.actualTotalKm !== undefined && data?.data?.actualTotalKm !== null && (
                    <Descriptions.Item label='Số km thực tế' span={colspan}>
                      <Text strong>{data?.data?.actualTotalKm + ' KM'}</Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label='Loại di chuyển'>
                    <Text strong>{data?.data?.transportationType?.name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Chi nhánh xe'>
                    <Text>{data?.data?.branch?.name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Ngày đi'>
                    <Text>{formatDate(data?.data?.departureDate)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Loại phương tiện'>
                    <Text>{data?.data?.vehicleType?.name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Ngày đến'>
                    <Text>{formatDate(data?.data?.arriveDate)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Ngày dự kiến về'>
                    <Text>{formatDate(data?.data?.expectedReturnDate)}</Text>
                  </Descriptions.Item>
                  {data?.data.confirmedType === ConfirmTypeEn.Confirmed &&
                    data.data.transportationTypeId === TransportationTypeEn.shuttle && (
                      <Descriptions.Item label='Phương tiện'>
                        <Text>{data?.data?.vehicle?.name}</Text>
                      </Descriptions.Item>
                    )}
                  <Descriptions.Item label='Chi phí dự kiến'>
                    <Text strong>
                      {data?.data?.estimatedCost ? formatToVND(data?.data?.estimatedCost) + ' VND' : ''}
                    </Text>
                  </Descriptions.Item>
                  {data?.data.confirmedType === ConfirmTypeEn.Confirmed &&
                    data.data.transportationTypeId === TransportationTypeEn.shuttle && (
                      <Descriptions.Item label='Tài xế'>
                        <Text>
                          {data?.data?.driver?.code}
                          {' - '}
                          {data?.data?.driver?.fullName}
                        </Text>
                      </Descriptions.Item>
                    )}
                  {data?.data.confirmedType === ConfirmTypeEn.Confirmed &&
                    data.data.transportationTypeId === TransportationTypeEn.selfSufficient && (
                      <Descriptions.Item label='Chi phí thực tế'>
                        <Text strong>{data?.data?.cost ? formatToVND(data?.data?.cost) + ' VND' : ''}</Text>
                      </Descriptions.Item>
                    )}
                </Descriptions>
                {data?.data.transportationTicketVerify && (
                  <Descriptions
                    layout='horizontal'
                    className='transportation-info-after'
                    labelStyle={{ width: '135px' }}
                    column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
                    extra={
                      <Text style={{ position: 'absolute', right: 10, top: 10 }} type='success'>
                        Người xác nhận cập nhật
                      </Text>
                    }
                  >
                    <Descriptions.Item label='Lý do đề nghị' span={colspan}>
                      <Text strong>{data?.data.transportationTicketVerify?.note}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Nơi đi' span={colspan}>
                      <Text>
                        {[
                          data?.data.transportationTicketVerify?.departureAddress ?? '',
                          data?.data.transportationTicketVerify?.departureIdInString ?? ''
                        ]
                          .filter((item) => item?.length > 0)
                          .join(', ')}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Nơi đến' span={colspan}>
                      <Text>
                        {[
                          data?.data.transportationTicketVerify?.destinationAddress ?? '',
                          data?.data.transportationTicketVerify?.destinationIdInString ?? ''
                        ]
                          .filter((item) => item?.length > 0)
                          .join(', ')}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Khoảng cách' span={colspan}>
                      <Text strong>
                        {data?.data.transportationTicketVerify?.totalKm
                          ? data?.data.transportationTicketVerify?.totalKm + ' KM'
                          : ''}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Ngày đi'>
                      <Text>{formatDate(data?.data.transportationTicketVerify?.departureDate)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Loại di chuyển'>
                      <Text strong>{data?.data.transportationTicketVerify?.transportationType?.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Ngày đến'>
                      <Text>{formatDate(data?.data.transportationTicketVerify?.arriveDate)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Loại phương tiện'>
                      <Text>{data?.data.transportationTicketVerify?.vehicleType?.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Ngày dự kiến về'>
                      <Text>{formatDate(data?.data.transportationTicketVerify?.expectedReturnDate)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Phương tiện'>
                      <Text>{data?.data.transportationTicketVerify?.vehicle?.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Chi phí dự kiến'>
                      <Text strong>
                        {data?.data.transportationTicketVerify?.estimatedCost
                          ? formatToVND(data?.data.transportationTicketVerify?.estimatedCost) + ' VND'
                          : ''}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Tài xế'>
                      <Text>
                        {data?.data.transportationTicketVerify?.driver?.code}
                        {' - '}
                        {data?.data.transportationTicketVerify?.driver?.fullName}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </Flex>
            </Spin>
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            <CollapseCustom
              title='Danh sách vận chuyển'
              titleLevel={5}
              bordered={false}
              isSegmented={false}
              items={transportationTicketDetailItems(data?.data.transportationTicketDetails || [])}
              expandIconPosition='end'
              className='transportation-ticket-list'
              collapsible='disabled'
            />
          </Fragment>
        )
      },
      {
        label: `Phiếu liên kết - ${businessTripTicketQuery.data?.data ? 'Công tác' : 'Ticket'}`,
        key: '2' as ActiveType,
        children: (
          <Fragment>
            <Flex justify='center'>
              <Title level={5}>Thông tin phiếu</Title>
            </Flex>
            {refTicketQuery.data && (
              <Spin size='small' spinning={refTicketQuery.isFetching}>
                <Descriptions
                  layout='horizontal'
                  labelStyle={{ width: '135px' }}
                  column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
                >
                  <Descriptions.Item label='Mã phiếu' span={colspan}>
                    <Text strong>{refTicketQuery?.data?.data?.ticketCode}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Danh mục' span={colspan}>
                    {refTicketQuery?.data?.data?.ticketCategory?.name}
                  </Descriptions.Item>
                  <Descriptions.Item label='Trạng thái' span={colspan}>
                    <TicketStatusTag ticketStatus={refTicketQuery?.data?.data.ticketStatus} />
                  </Descriptions.Item>
                  <Descriptions.Item label='Ngày tạo'>{formatDate(refTicketQuery?.data?.data?.date)}</Descriptions.Item>
                  <Descriptions.Item label='Người tạo'>
                    <Text>{refTicketQuery?.data?.data?.createdByObj?.name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Người xử lý' span={colspan}>
                    <Text>{refTicketQuery?.data?.data?.handlerObj?.name || ''}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Giải pháp' span={colspan}>
                    <Text>
                      {Parser(refTicketQuery?.data?.data?.ticketSolutions?.find((x) => x.isApproval)?.solution || '')}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Nội dung yêu cầu' span={colspan}>
                    <Text> {Parser(refTicketQuery?.data?.data?.remark || '')}</Text>
                  </Descriptions.Item>
                  {data?.data.ticketStatus.id === TicketStatus.Reject && (
                    <Descriptions.Item label='Lý do từ chối' span={colspan}>
                      <Text strong>{data?.data?.approvals?.reverse()?.[0].note}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Spin>
            )}
            {businessTripTicketQuery.data && (
              <Spin size='small' spinning={businessTripTicketQuery.isFetching}>
                <Descriptions column={2} labelStyle={{ width: '110px' }} layout='horizontal'>
                  <Descriptions.Item label='Mã phiếu' span={colspan}>
                    <Text strong>{businessTripTicketQuery?.data?.data?.code}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Trạng thái' span={colspan}>
                    <HRTicketStatusTag
                      ticketStatus={businessTripTicketQuery.data?.data.ticketStatus}
                      erpKey={businessTripTicketQuery.data?.data.erpKey}
                      isConfirm={businessTripTicketQuery.data?.data.isConfirm}
                      hrConfirmedType={businessTripTicketQuery.data?.data.hrConfirmedType}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label='Ngày tạo'>
                    {formatDate(businessTripTicketQuery?.data?.data?.createdDate)}
                  </Descriptions.Item>
                  <Descriptions.Item label='Người tạo'>
                    <Text>{businessTripTicketQuery?.data?.data?.createdBy?.fullName}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Nơi đi'>
                    <Text>{Parser(businessTripTicketQuery.data?.data?.departureIdInString || '')}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Nơi đến'>
                    <Text>{Parser(businessTripTicketQuery.data?.data?.destinationIdInString || '')}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Phiếu liên kết'>
                    <Text strong>{businessTripTicketQuery.data?.data?.refTicketCode}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Chi tiết công việc'>
                    <Text strong>{Parser(businessTripTicketQuery.data?.data?.note || '')}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Spin>
            )}
          </Fragment>
        )
      }
    ];

    return !businessTripTicketQuery.data?.data && !refTicketQuery.data?.data ? items.slice(0, 1) : items;
  };

  return (
    <div className='transportation-ticket-detail-wrapper'>
      <BtnHrHandle type='transportationTicket' />

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      <Tabs
        defaultActiveKey='1'
        type='card'
        size='middle'
        activeKey={activeKey}
        items={tabsItems()}
        onTabClick={(activeKey: string) => setActiveKey(activeKey as ActiveType)}
      />
    </div>
  );
}

const renderLocationText = (address: string | undefined, location: any, idInString: any, fallbackText: string) => {
  const parts: string[] = [];

  // Add address if exists and is string
  if (address && typeof address === 'string') {
    parts.push(address);
  }

  // Add location info if distance exists
  if (location) {
    const locationParts = [location.type, location.name, location.province?.type, location.province?.name].filter(
      (part) => part && typeof part === 'string'
    );

    if (locationParts.length > 0) {
      parts.push(locationParts.join(' '));
    }
  }

  // Add idInString if exists and is string (fallback)
  if (!location && idInString && typeof idInString === 'string') {
    parts.push(idInString);
  }

  return parts.length > 0 ? parts.join(', ') : fallbackText;
};
